import { useState } from 'react';
import clsx from 'clsx';

import { Cluster, ModalProps } from '@typings';
import { cn, getFormattedMemory, getGpuFromResourcePreset, noop } from '@utils';

import { Button, Field, Modal, Table } from '@components';
import { GpuModels } from '@components/Ui';

type Props = ModalProps & {
  resourcePresets: Cluster.ResourcePreset[];
  onApply?: (
    presetName: string,
    resourcePreset: Cluster.ResourcePreset,
  ) => void;
};

export const JobConstructorPresetsModal = ({
  resourcePresets,
  onApply = noop,
  closeModal = noop,
}: Props) => {
  const [activeResourcePreset, setActiveResourcePreset] =
    useState<Cluster.ResourcePreset | null>(null);

  const handlePresetApply = () => {
    /**
     * Apply button is disabled while no active resource preset,
     * that is why non-null assertion operator is used
     */
    const resourcePreset = activeResourcePreset!;

    onApply(resourcePreset.name, resourcePreset!);
    closeModal();
  };

  const getBooleanValue = (value: boolean | undefined | null) =>
    value ? '+' : '-';

  const makeResourcePreset = (resourcePreset: Cluster.ResourcePreset) => {
    const {
      name,
      cpu,
      schedulerEnabled,
      creditsPerHour,
      memory,
      isPreemptible,
    } = resourcePreset;
    const gpuModels = getGpuFromResourcePreset(resourcePreset, {
      withPlaceholder: true,
    });
    const isActive = name === activeResourcePreset?.name;
    const className = cn(
      'cursor-pointer border-neural-02 py-4 transition-colors group-hover:bg-neural-01',
      { 'bg-neural-01': isActive },
    );

    return (
      <Table.Row
        key={name}
        onClick={() => setActiveResourcePreset(resourcePreset)}
        className="group cursor-pointer"
      >
        <Table.Cell className={clsx(className, 'pr-0')}>
          <Field.Checkbox variant="radio" checked={isActive} />
        </Table.Cell>
        <Table.Cell className={className}>{name}</Table.Cell>
        <Table.Cell className={className}>{cpu}</Table.Cell>
        <Table.Cell className={className}>
          {getFormattedMemory(memory)}
        </Table.Cell>
        <Table.Cell className={className}>
          <GpuModels models={gpuModels} />
        </Table.Cell>
        <Table.Cell className={className}>
          {getBooleanValue(schedulerEnabled)}
        </Table.Cell>
        <Table.Cell className={className}>
          {getBooleanValue(isPreemptible)}
        </Table.Cell>
        <Table.Cell className={className}>{creditsPerHour}</Table.Cell>
      </Table.Row>
    );
  };

  return (
    <Modal.Content title="Choose Resource Preset" className="w-[1024px]">
      <Table className="mt-4 w-full auto-rows-min grid-cols-[auto_max-content_repeat(2,min-content)_repeat(2,max-content)_repeat(2,min-content)] overflow-auto border border-neural-02">
        <Table.Header>
          <Table.Row>
            <Table.Head className="py-3" />
            <Table.Head className="py-3">Name</Table.Head>
            <Table.Head className="py-3">CPU</Table.Head>
            <Table.Head className="py-3">Memory</Table.Head>
            <Table.Head className="py-3">GPU</Table.Head>
            <Table.Head className="py-3">Round-robin</Table.Head>
            <Table.Head className="py-3">Preemptible</Table.Head>
            <Table.Head className="py-3">Price/Hour</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>{resourcePresets.map(makeResourcePreset)}</Table.Body>
      </Table>
      <Modal.Footer sticky>
        <Button
          className="px-10"
          disabled={!activeResourcePreset}
          onClick={handlePresetApply}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal.Content>
  );
};
